import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface PortalMenuProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  onRequestClose: () => void;
  children: React.ReactNode;
  className?: string;
  role?: React.AriaRole;
  ariaLabel?: string;
  align?: 'start' | 'end';
  placement?: 'auto' | 'top' | 'bottom';
  offset?: number;
  matchAnchorWidth?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

/**
 * Renders a floating menu outside of clipping ancestors such as tables and
 * scroll regions. The menu follows its trigger while the page or a nested
 * scroll container moves, and opens above the trigger when the lower viewport
 * edge does not have enough room.
 */
export function PortalMenu({
  anchorRef,
  onRequestClose,
  children,
  className = '',
  role,
  ariaLabel,
  align = 'end',
  placement = 'auto',
  offset = 4,
  matchAnchorWidth = false,
  onClick,
}: PortalMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [menuHeight, setMenuHeight] = useState(0);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const update = () => setAnchorRect(anchor.getBoundingClientRect());
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!anchorRect || !menuRef.current) return;
    const nextHeight = menuRef.current.getBoundingClientRect().height;
    setMenuHeight((current) => (current === nextHeight ? current : nextHeight));
  }, [anchorRect]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      onRequestClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onRequestClose();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [anchorRef, onRequestClose]);

  if (!anchorRect) return null;

  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const spaceAbove = anchorRect.top;
  const shouldOpenUp = placement === 'top'
    || (placement === 'auto'
      && spaceBelow < Math.max(menuHeight + offset, 200)
      && spaceAbove > spaceBelow);
  const horizontalPosition = align === 'end'
    ? { right: Math.max(8, window.innerWidth - anchorRect.right) }
    : { left: Math.max(8, anchorRect.left) };

  return createPortal(
    <div
      ref={menuRef}
      role={role}
      aria-label={ariaLabel}
      onClick={onClick}
      style={{
        position: 'fixed',
        maxWidth: 'calc(100vw - 16px)',
        ...(matchAnchorWidth ? { width: anchorRect.width } : {}),
        ...horizontalPosition,
        ...(shouldOpenUp
          ? { bottom: window.innerHeight - anchorRect.top + offset }
          : { top: anchorRect.bottom + offset }),
      }}
      className={`z-50 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg ${className}`}
    >
      {children}
    </div>,
    document.body,
  );
}
